import clsx from 'clsx';
import React, { useState } from 'react';
import {
  Button,
  Callout,
  EmptyState,
  IconPhone,
  ListPage,
  Pagination,
  Spinner,
  StatusPill,
  Table,
  TableSkeletonRows,
  Tag,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../ui/v2';
import AdminCallDialogs, { type AdminCallDialogType } from './AdminCallDialogs';
import { TableSearch } from './TableSearch';
import {
  ADMIN_CALLS_PAGE_SIZE,
  adminCallDirectionLabel,
  adminCallDirectionTone,
  adminCallSentimentTone,
  adminCallStatusLabel,
  adminCallStatusTone,
  formatAdminCallCost,
  formatAdminCallCreatedAt,
  formatAdminCallDuration,
} from './adminCallsModel';
import type { AdminCallSortKey } from './platformAdminOrdering';
import type { AdminCallsState } from './useAdminCalls';

const COLUMNS: {
  key: AdminCallSortKey | 'transcript' | 'summary';
  label: string;
  align?: 'left' | 'right';
  sortable?: boolean;
}[] = [
  { key: 'id', label: 'ID', sortable: true },
  { key: 'storeName', label: 'Store Name', sortable: true },
  { key: 'storeId', label: 'Store ID', sortable: true },
  { key: 'customer', label: 'Customer', sortable: true },
  { key: 'direction', label: 'Direction', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'fromNumber', label: 'From', sortable: true },
  { key: 'duration', label: 'Duration', sortable: true },
  { key: 'cost', label: 'Cost', align: 'right', sortable: true },
  { key: 'sentiment', label: 'Sentiment', sortable: true },
  { key: 'transcript', label: 'Transcript' },
  { key: 'summary', label: 'Summary' },
  { key: 'createdAt', label: 'Created', sortable: true },
];

const Placeholder: React.FC<{ value: React.ReactNode }> = ({ value }) =>
  value === '—' ? <span className="text-v2-fg-3">—</span> : <>{value}</>;

const AdminCallsTab: React.FC<AdminCallsState> = ({
  calls,
  loading,
  page,
  totalPages,
  count,
  search,
  appliedSearch,
  listError,
  sort,
  setPage,
  onSearchChange,
  toggleSort,
}) => {
  const [selectedCall, setSelectedCall] = useState<any | null>(null);
  const [dialogType, setDialogType] = useState<AdminCallDialogType | null>(
    null,
  );
  const showInitialLoading = loading && calls.length === 0;
  const isRefreshing = loading && calls.length > 0;
  const filtering = appliedSearch.length > 0;

  const openDialog = (call: any, type: AdminCallDialogType) => {
    setSelectedCall(call);
    setDialogType(type);
  };
  const closeDialog = () => {
    setSelectedCall(null);
    setDialogType(null);
  };

  return (
    <ListPage
      title="Calls (Admin)"
      actions={
        isRefreshing && (
          <span
            role="status"
            className="inline-flex items-center gap-1.5 text-v2-12 text-v2-fg-3"
          >
            <Spinner />
            Updating calls...
          </span>
        )
      }
      toolbar={
        <div className="flex min-h-11 items-center px-4 py-2 lg:px-6">
          <TableSearch
            label="Search calls"
            placeholder="Search store, customer, phone, or call id…"
            value={search}
            onChange={onSearchChange}
          />
        </div>
      }
    >
      {listError && (
        <div className="px-4 pt-4 lg:px-6">
          <Callout tone="danger">{listError}</Callout>
        </div>
      )}

      {!showInitialLoading && calls.length === 0 ? (
        <EmptyState
          icon={<IconPhone />}
          title={
            filtering
              ? 'No calls match that search'
              : "You don't have any calls at the moment"
          }
          description={
            filtering
              ? 'Try a store, customer, phone number, or call id.'
              : "When calls appear, they'll show up here."
          }
        />
      ) : (
        <>
          {showInitialLoading && (
            <p role="status" className="sr-only">
              Loading calls…
            </p>
          )}
          <div
            className={clsx(
              'v2-scroll overflow-x-auto transition-opacity',
              isRefreshing && 'opacity-70',
            )}
          >
            <Table className="min-w-[1500px]">
              <THead>
                <tr>
                  {COLUMNS.map((column) => (
                    <TH
                      key={column.key}
                      align={column.align}
                      sortable={column.sortable && !showInitialLoading}
                      active={column.sortable && sort.key === column.key}
                      dir={sort.direction}
                      onSort={() => toggleSort(column.key as AdminCallSortKey)}
                    >
                      {column.label}
                    </TH>
                  ))}
                </tr>
              </THead>
              <TBody>
                {showInitialLoading ? (
                  <TableSkeletonRows cols={COLUMNS.length} />
                ) : (
                  calls.map((call: any, idx: number) => {
                    const directionLabel = adminCallDirectionLabel(
                      call.direction,
                    );
                    const statusLabel = adminCallStatusLabel(call.call_status);
                    const combinedCost = call.call_cost?.combined_cost;
                    return (
                      <TR key={call.id || idx}>
                        <TD num className="font-medium">
                          {call.id}
                        </TD>
                        <TD className="font-medium">
                          <Placeholder value={call.store_name || '—'} />
                        </TD>
                        <TD num>
                          <Placeholder value={call.store_id || '—'} />
                        </TD>
                        <TD num>
                          <Placeholder value={call.customer_id || '—'} />
                        </TD>
                        <TD>
                          {directionLabel ? (
                            <Tag tone={adminCallDirectionTone(call.direction)}>
                              {directionLabel}
                            </Tag>
                          ) : (
                            <Placeholder value="—" />
                          )}
                        </TD>
                        <TD>
                          {statusLabel === '—' ? (
                            <Placeholder value="—" />
                          ) : (
                            <StatusPill
                              tone={adminCallStatusTone(call.call_status)}
                            >
                              {statusLabel}
                            </StatusPill>
                          )}
                        </TD>
                        <TD num className="whitespace-nowrap">
                          <Placeholder value={call.from_number || '—'} />
                        </TD>
                        <TD num>
                          <Placeholder value={formatAdminCallDuration(call)} />
                        </TD>
                        <TD num align="right">
                          {combinedCost != null ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`View cost breakdown for call #${call.id}`}
                              onClick={() => openDialog(call, 'cost')}
                              className="-mr-2.5 v2-num"
                            >
                              {formatAdminCallCost(combinedCost)}
                            </Button>
                          ) : (
                            <Placeholder value="—" />
                          )}
                        </TD>
                        <TD>
                          {call.user_sentiment ? (
                            <StatusPill
                              tone={adminCallSentimentTone(call.user_sentiment)}
                            >
                              {call.user_sentiment}
                            </StatusPill>
                          ) : (
                            <Placeholder value="—" />
                          )}
                        </TD>
                        <TD>
                          {call.transcript ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`View transcript for call #${call.id}`}
                              onClick={() => openDialog(call, 'transcript')}
                              className="-ml-2.5"
                            >
                              View
                            </Button>
                          ) : (
                            <Placeholder value="—" />
                          )}
                        </TD>
                        <TD>
                          {call.call_summary ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`View summary for call #${call.id}`}
                              onClick={() => openDialog(call, 'summary')}
                              className="-ml-2.5"
                            >
                              View
                            </Button>
                          ) : (
                            <Placeholder value="—" />
                          )}
                        </TD>
                        <TD num className="whitespace-nowrap">
                          <Placeholder
                            value={formatAdminCallCreatedAt(call.created_at)}
                          />
                        </TD>
                      </TR>
                    );
                  })
                )}
              </TBody>
            </Table>
          </div>
        </>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        totalCount={count}
        pageSize={ADMIN_CALLS_PAGE_SIZE}
        onPage={setPage}
        noun="calls"
      />

      <AdminCallDialogs
        call={selectedCall}
        type={dialogType}
        onClose={closeDialog}
      />
    </ListPage>
  );
};

export default AdminCallsTab;
